import {
  Box,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@wegroup/design-system';
import React, { PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';
import { toEuro } from '../../../../../../utils/stringUtils';
import DropdownButton from '../../shared/DropdownButton';
import BorderWrapper from '../BorderWrapper';

interface Props {
  last?: boolean;
  withBackground?: boolean;
  values: { nodes: React.ReactNode[]; extraNodes?: React.ReactNode[] };
  subValues?: ({ values: React.ReactNode[]; title: string } | null)[];
  bestOfferIndex: number;
}

const ComparisonMatrixItem: React.FC<React.PropsWithChildren<Props>> = ({
  last,
  withBackground,
  values,
  subValues,
  bestOfferIndex,
  children,
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const hasSubvalues = subValues && subValues.length;

  const getSubValueContent = (value: React.ReactNode) => {
    if (value) {
      if (typeof value === 'number') return toEuro(value);
      return value;
    }
    return <p>-</p>;
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Title last={last} withBackground={withBackground}>
        <HStack
          w="100%"
          justify="space-between"
          zIndex="2"
          onClick={() => setIsSubMenuOpen((isOpen) => !isOpen)}
          cursor={hasSubvalues ? 'pointer' : 'initial'}
        >
          <HStack>{children}</HStack>
          {hasSubvalues && (
            <DropdownButton color="black" isOpen={isSubMenuOpen} />
          )}
        </HStack>
      </Title>
      {values.nodes.map((value, index) => (
        <StyledDesktopBorderWrapper
          key={index + (value ? value?.toString() : 'item')}
          withBackground={withBackground}
          lastRow={!withBackground && last && !isSubMenuOpen}
          lastRowSubMenuOpen={!withBackground && last && isSubMenuOpen}
          bestOffer={index === bestOfferIndex}
        >
          <ContentWrapper
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            extraNode={values.extraNodes?.[index]}
          >
            {value}
          </ContentWrapper>
        </StyledDesktopBorderWrapper>
      ))}
      <LastWrapper last={last} withBackground={withBackground} />

      {isSubMenuOpen &&
        subValues?.map((object, index) =>
          object ? (
            <>
              <HStack
                height="100%"
                alignItems="center"
                pos="relative"
                ml="8.1rem"
                pb={
                  !withBackground && last && subValues.length - 1 === index
                    ? '2.4rem'
                    : '0'
                }
              >
                <Box
                  w="2.4rem"
                  h="5rem"
                  border="2px solid"
                  borderRight="none"
                  borderTop="none"
                  borderColor="gray.100"
                  pos="absolute"
                  right="calc(100% + 1.8rem)"
                  bottom="calc(100% - 2.5rem)"
                  zIndex="1"
                />
                <Text textStyle="smallBody">{object.title}</Text>
              </HStack>
              {object.values.map((value, subValueIndex) => (
                <SubValueWrapper
                  key={value?.toString()}
                  bestOffer={subValueIndex === bestOfferIndex}
                  lastRow={
                    !withBackground && last && subValues.length - 1 === index
                  }
                >
                  <ContentWrapper
                    isOpen={isOpen}
                    onClose={onClose}
                    onOpen={onOpen}
                  >
                    {getSubValueContent(value)}
                  </ContentWrapper>
                </SubValueWrapper>
              ))}
              <LastWrapper last={last} withBackground={withBackground} />
            </>
          ) : (
            <p>-</p>
          ),
        )}
    </>
  );
};

interface ContentWrapperProps {
  onOpen: () => void;
  onClose: () => void;
  isOpen: boolean;
  extraNode?: React.ReactNode;
}

const ContentWrapper: React.FC<PropsWithChildren<ContentWrapperProps>> = ({
  onOpen,
  onClose,
  isOpen,
  children,
  extraNode,
}) => {
  const handleClick = () => {
    if (isOpen) onClose();
    else onOpen();
  };

  return (
    <VStack
      w="calc(100% - 4.8rem)"
      h="100%"
      borderTop="1px solid"
      mx="6"
      py="4"
      borderColor="gray.200"
      cursor={extraNode ? 'pointer' : 'default'}
      onClick={handleClick}
      spacing="4"
      position="relative"
      _hover={
        extraNode
          ? {
              bg: 'gray.ultraLight',
            }
          : undefined
      }
      transition="0.2s"
    >
      {children}
      {isOpen && extraNode}
    </VStack>
  );
};

const StyledDesktopBorderWrapper = styled(BorderWrapper)<{
  withBackground?: boolean;
  lastRow?: boolean;
  bestOffer?: boolean;
  lastRowSubMenuOpen?: boolean;
}>`
  background: ${({ withBackground }) =>
    withBackground ? '#f7f8fb' : 'transparent'};

  ${({ lastRow, bestOffer }) =>
    lastRow &&
    css`
      > div {
        padding-bottom: 2.4rem;
        border-bottom: 1px solid
          ${({ theme }) => (bestOffer ? theme.primary[500] : theme.gray[200])};
        border-bottom-left-radius: 6px;
        border-bottom-right-radius: 6px;
      }
    `};

  /* Fix issue with stars moving when submenu is opened */
  ${({ lastRowSubMenuOpen }) =>
    lastRowSubMenuOpen &&
    css`
      > div {
        padding-bottom: 0.8rem;
      }
    `}

  ${({ bestOffer }) =>
    bestOffer &&
    css`
      > div {
        border-left: 1px solid ${({ theme }) => theme.primary[500]};
        border-right: 1px solid ${({ theme }) => theme.primary[500]};
      }
    `}
`;

const SubValueWrapper = styled(StyledDesktopBorderWrapper)`
  > div > div {
    border: none;
  }
`;

const Title = styled(StyledDesktopBorderWrapper)<{ last?: boolean }>`
  width: 100%;
  padding: 0 2.4rem;
  font-size: 1.8rem;
  line-height: 120%;

  > div {
    padding: 1.6rem 0;
    border: none;
    justify-content: flex-start;
    align-items: flex-start;

    border-top: 1px solid ${({ theme }) => theme.gray[200]};
  }

  border-bottom-left-radius: ${({ last }) => (last ? '6px' : '0')};
`;

const LastWrapper = styled(StyledDesktopBorderWrapper)<{ last?: boolean }>`
  width: 50% !important;
  padding: 0;
  > div {
    border: none;
  }

  border-bottom-right-radius: ${({ last }) => (last ? '6px' : '0')};
`;

export default ComparisonMatrixItem;
